import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Save, X, ToggleLeft, ToggleRight, Trash2, KeyRound, MessageCircle, Building2, Eye, Shield } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

import { API_BASE, apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS, getRolePermissions } from '../lib/permissions';

interface AdminUserItem {
  id: number;
  username: string;
  role: string;
  display_name: string;
  phone: string;
  is_active: number;
  created_at: number;
  supplier_tag?: string;
  supplier_label?: string;
}

interface WhatsAppResult {
  success: boolean;
  sent?: boolean;
  error?: string;
}

const roleLabels: Record<string, string> = {
  super_admin: '系統管理員',
  admin: '管理員',
  supplier: '產品供應商',
  estate_manager: '屋苑管理員',
};

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'admin',
    display_name: '',
    phone: '',
    is_active: true,
    supplierTag: '',
    supplierLabel: '',
  });
  const [formEstateIds, setFormEstateIds] = useState<number[]>([]);

  const [estates, setEstates] = useState<{ id: number; name: string }[]>([]);
  const [editEstateIds, setEditEstateIds] = useState<number[]>([]);
  const [previewUser, setPreviewUser] = useState<AdminUserItem | null>(null);
  const [whatsappStatus, setWhatsappStatus] = useState<string>('');

  const [editData, setEditData] = useState<Partial<AdminUserItem> & { supplierTag?: string; supplierLabel?: string }>({});
  const [resetPasswordId, setResetPasswordId] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [sendLoading, setSendLoading] = useState(false);

  const token = localStorage.getItem('admin_token');
  const { can } = usePermissions();

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchUsers();
    fetchEstates();
  }, [token, navigate]);

  const fetchEstates = async () => {
    try {
      const res = await apiFetch('/api/public/admin/service-estates');
      if (res.ok) {
        const data = await res.json();
        setEstates(data.map((e: any) => ({ id: e.id, name: e.name })));
      }
    } catch { /* ignore */ }
  };

  const saveUserEstatesById = async (userId: number, estateIds: number[]) => {
    try {
      const res = await apiFetch(`/api/public/admin/users/${userId}/estates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estateIds }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '屋苑權限更新失敗');
      }
    } catch {
      setError('屋苑權限更新失敗');
    }
  };

  const loadUserEstates = async (userId: number) => {
    try {
      const res = await apiFetch(`/api/public/admin/users/${userId}/estates`);
      if (res.ok) {
        const data = await res.json();
        setEditEstateIds((data.estateIds || []).map(Number));
      }
    } catch {
      setEditEstateIds([]);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setUsers(await res.json());
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      }
    } catch (e) {
      setError('獲取用戶失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.username || !formData.password) {
      setError('請填寫帳號和密碼');
      return;
    }
    if (formData.role === 'supplier' && !formData.supplierTag.trim()) {
      setError('請填寫 Supplier Tag');
      return;
    }
    try {
      const body: any = {
        ...formData,
        is_active: formData.is_active ? 1 : 0,
      };
      if (body.role !== 'supplier') {
        delete body.supplierTag;
        delete body.supplierLabel;
      }
      const res = await fetch(`${API_BASE}/api/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        if (formData.role === 'estate_manager' && data.id && formEstateIds.length > 0) {
          await saveUserEstatesById(data.id, formEstateIds);
        }
        setShowForm(false);
        setFormData({ username: '', password: '', role: 'admin', display_name: '', phone: '', is_active: true, supplierTag: '', supplierLabel: '' });
        setFormEstateIds([]);
        if (data.whatsappSent) {
          setWhatsappStatus('登入資料已發送至 WhatsApp');
          setTimeout(() => setWhatsappStatus(''), 5000);
        }
        fetchUsers();
      } else {
        const data = await res.json();
        setError(data.error || '創建失敗');
      }
    } catch (e) {
      setError('創建失敗');
    }
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    if (editData.role === 'supplier' && !editData.supplierTag?.trim()) {
      setError('請填寫 Supplier Tag');
      return;
    }
    try {
      const body: any = { ...editData };
      if (body.role !== 'supplier') {
        delete body.supplierTag;
        delete body.supplierLabel;
      }
      const res = await fetch(`${API_BASE}/api/admin/users/${editingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const originalRole = users.find(u => u.id === editingId)?.role;
        if (editingId) {
          if (editData.role === 'estate_manager') {
            await saveUserEstatesById(editingId, editEstateIds);
          } else if (originalRole === 'estate_manager') {
            await saveUserEstatesById(editingId, []);
          }
        }
        setEditingId(null);
        setEditEstateIds([]);
        fetchUsers();
      }
    } catch (e) {
      setError('更新失敗');
    }
  };

  const handleToggle = async (id: number, current: number) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_active: !current }),
      });
      if (res.ok) fetchUsers();
    } catch (e) {
      setError('操作失敗');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('確定要刪除此用戶嗎？')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchUsers();
      } else {
        const data = await res.json();
        setError(data.error || '刪除失敗');
      }
    } catch (e) {
      setError('刪除失敗');
    }
  };

  const startEdit = (user: AdminUserItem) => {
    setEditingId(user.id);
    setEditData({
      display_name: user.display_name,
      phone: user.phone,
      role: user.role,
      is_active: user.is_active,
      supplierTag: user.supplier_tag || '',
      supplierLabel: user.supplier_label || '',
    });
    setEditEstateIds([]);
    loadUserEstates(user.id);
  };

  const MODULE_LABELS: Record<string, string> = {
    dashboard: '儀表板',
    orders: '訂單',
    customers: '客戶',
    products: '產品',
    packages: '套餐',
    media: '媒體庫',
    estates: '屋苑',
    users: '用戶',
    settings: '系統設置',
    broadcast: '廣播',
    referrals: '推薦',
    conversations: 'WhatsApp 對話',
    audit: '操作日誌',
    cms: 'CMS 內容',
    ahjim: '趣味插圖',
  };

  const getPermissionMatrix = (role: string) => {
    const perms = getRolePermissions(role);
    const matrix: Record<string, { read: boolean; write: boolean }> = {};
    perms.forEach(p => {
      const [module, action] = p.split(':');
      if (!module) return;
      if (!matrix[module]) matrix[module] = { read: false, write: false };
      if (action === 'read') matrix[module].read = true;
      if (['write', 'delete', 'complete', 'send'].includes(action)) matrix[module].write = true;
    });
    return matrix;
  };

  const EstateSelector = ({ selectedIds, onChange }: { selectedIds: number[]; onChange: (ids: number[]) => void }) => (
    <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
      {estates.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 col-span-full">暫無屋苑，請先到「屋苑管理」新增。</p>
      ) : (
        estates.map(estate => (
          <label key={estate.id} className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedIds.includes(estate.id)}
              onChange={e => {
                if (e.target.checked) onChange([...selectedIds, estate.id]);
                else onChange(selectedIds.filter(id => id !== estate.id));
              }}
              className="w-4 h-4"
            />
            <span className="text-sm text-gray-800 dark:text-gray-200">{estate.name}</span>
          </label>
        ))
      )}
    </div>
  );

  const handleResetPassword = async () => {
    if (!resetPasswordId || !newPassword) {
      setError('請輸入新密碼');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${resetPasswordId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ password: newPassword }),
      });
      if (res.ok) {
        setResetPasswordId(null);
        setNewPassword('');
        setSuccess('密碼重置成功');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await res.json();
        setError(data.error || '重置失敗');
      }
    } catch (e) {
      setError('重置失敗');
    }
  };

  return (
    <AdminLayout currentPage="users">
      <div>
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">✕</button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center">
            <span>{success}</span>
            <button onClick={() => setSuccess(null)} className="text-green-500 hover:text-green-700">✕</button>
          </div>
        )}
        {whatsappStatus && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center">
            <span>{whatsappStatus}</span>
            <button onClick={() => setWhatsappStatus('')} className="text-green-500 hover:text-green-700">✕</button>
          </div>
        )}

        {/* Action Buttons */}
        {can(PERMISSIONS.USERS_WRITE) && (
          <div className="mb-6 flex gap-3">
            <button
              onClick={() => setShowForm(!showForm)}
              className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              {showForm ? '取消' : '新增管理員'}
            </button>
            <button
              onClick={() => { setShowSendModal(true); setSelectedUserIds([]); }}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              發送登入資料到 WhatsApp
            </button>
          </div>
        )}

        {/* Create Form */}
        {showForm && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6 border-l-4 border-blue-500">
            <h3 className="text-lg font-bold mb-4 dark:text-white">新增管理員</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">帳號 *</label>
                <input
                  value={formData.username}
                  onChange={e => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">密碼 *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">角色</label>
                <select
                  value={formData.role}
                  onChange={e => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                >
                  <option value="admin">管理員</option>
                  <option value="supplier">產品供應商</option>
                  <option value="estate_manager">屋苑管理員</option>
                  <option value="super_admin">系統管理員</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">顯示名稱</label>
                <input
                  value={formData.display_name}
                  onChange={e => setFormData({ ...formData, display_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="顯示名稱"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">WhatsApp</label>
                <input
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="852xxxxxxx"
                />
              </div>
              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm dark:text-gray-300">啟用</span>
                </label>
              </div>
            </div>
            {formData.role === 'supplier' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Supplier Tag *</label>
                  <input
                    value={formData.supplierTag}
                    onChange={e => setFormData({ ...formData, supplierTag: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                    placeholder="fungtai"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">顯示名稱 / Supplier Label</label>
                  <input
                    value={formData.supplierLabel}
                    onChange={e => setFormData({ ...formData, supplierLabel: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                    placeholder="豐泰"
                  />
                </div>
              </div>
            )}
            {formData.role === 'estate_manager' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                  <Building2 size={16} className="text-purple-600" />
                  服務屋苑
                </label>
                <EstateSelector selectedIds={formEstateIds} onChange={setFormEstateIds} />
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <button onClick={handleCreate} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">創建</button>
              <button onClick={() => { setShowForm(false); setFormEstateIds([]); }} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">取消</button>
            </div>
          </div>
        )}

        {/* Users Table */}
        {loading ? (
          <div className="bg-white dark:bg-gray-800 rounded shadow p-8 text-center text-gray-500 dark:text-gray-400">加載中...</div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">帳號</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">名稱</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">角色</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tag</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Label</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">WhatsApp</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">狀態</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {users.map(user => (
                  <React.Fragment key={user.id}>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm">{user.id}</td>
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{user.username}</td>
                      <td className="px-4 py-3">
                        {editingId === user.id ? (
                          <input
                            value={editData.display_name || ''}
                            onChange={e => setEditData({ ...editData, display_name: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          />
                        ) : (
                          user.display_name || '-'
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {editingId === user.id ? (
                          <select
                            value={editData.role || ''}
                            onChange={e => setEditData({ ...editData, role: e.target.value })}
                            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          >
                            <option value="admin">管理員</option>
                            <option value="supplier">產品供應商</option>
                            <option value="estate_manager">屋苑管理員</option>
                            <option value="super_admin">系統管理員</option>
                          </select>
                        ) : (
                          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                            user.role === 'super_admin' ? 'bg-red-100 text-red-800' :
                            user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                            user.role === 'estate_manager' ? 'bg-purple-100 text-purple-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {roleLabels[user.role] || user.role}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {user.role === 'supplier' && user.supplier_tag && (
                          <span className="text-xs font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{user.supplier_tag}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {user.role === 'supplier' && user.supplier_label && (
                          <span className="text-xs text-gray-600 dark:text-gray-400">{user.supplier_label}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {editingId === user.id ? (
                          <input
                            value={editData.phone || ''}
                            onChange={e => setEditData({ ...editData, phone: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          />
                        ) : (
                          user.phone || '-'
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {can(PERMISSIONS.USERS_WRITE) ? (
                          <button
                            onClick={() => editingId !== user.id && handleToggle(user.id, user.is_active)}
                            className={`flex items-center gap-1 text-sm ${user.is_active ? 'text-green-600' : 'text-gray-400'}`}
                          >
                            {user.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                          </button>
                        ) : (
                          <span className={`flex items-center gap-1 text-sm ${user.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                            {user.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                            {user.is_active ? '啟用' : '停用'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {can(PERMISSIONS.USERS_WRITE) ? (
                          editingId === user.id ? (
                            <div className="flex gap-2">
                              <button onClick={handleUpdate} className="text-green-600 hover:text-green-800"><Save size={18} /></button>
                              <button onClick={() => { setEditingId(null); setEditEstateIds([]); }} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <button onClick={() => startEdit(user)} className="text-blue-600 hover:text-blue-800" title="編輯"><Edit2 size={18} /></button>
                              <button onClick={() => setPreviewUser(user)} className="text-cyan-600 hover:text-cyan-800" title="權限預覽"><Eye size={18} /></button>
                              <button onClick={() => { setResetPasswordId(user.id); setNewPassword(''); }} className="text-amber-600 hover:text-amber-800" title="重置密碼"><KeyRound size={18} /></button>
                              <button onClick={() => handleDelete(user.id)} className="text-red-600 hover:text-red-800" title="刪除"><Trash2 size={18} /></button>
                            </div>
                          )
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                    </tr>
                    {editingId === user.id && editData.role === 'estate_manager' && (
                      <tr className="bg-purple-50/50 dark:bg-purple-900/10">
                        <td colSpan={9} className="px-4 py-3">
                          <div className="mb-2 text-sm font-medium text-purple-700 dark:text-purple-300 flex items-center gap-2">
                            <Building2 size={16} /> 服務屋苑
                          </div>
                          <EstateSelector selectedIds={editEstateIds} onChange={setEditEstateIds} />
                        </td>
                      </tr>
                    )}
                    {editingId === user.id && editData.role === 'supplier' && (
                      <tr className="bg-green-50/50 dark:bg-green-900/10">
                        <td colSpan={9} className="px-4 py-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Supplier Tag *</label>
                              <input
                                value={editData.supplierTag || ''}
                                onChange={e => setEditData({ ...editData, supplierTag: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm"
                                placeholder="fungtai"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">顯示名稱 / Supplier Label</label>
                              <input
                                value={editData.supplierLabel || ''}
                                onChange={e => setEditData({ ...editData, supplierLabel: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm"
                                placeholder="豐泰"
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">沒有用戶</div>
            )}
          </div>
        )}

        {/* Reset Password Modal */}
        {resetPasswordId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm w-full">
              <h3 className="text-lg font-bold mb-4 dark:text-white">重置密碼</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">新密碼</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="輸入新密碼"
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { setResetPasswordId(null); setNewPassword(''); }}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  取消
                </button>
                {can(PERMISSIONS.USERS_WRITE) && (
                  <button
                    onClick={handleResetPassword}
                    className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                  >
                    確認重置
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Send Credentials Modal */}
        {showSendModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full">
              <h3 className="text-lg font-bold mb-4 dark:text-white">選擇發送對象</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                勾選要發送登入資料的用戶（只顯示有 WhatsApp 號碼的 admin/supplier）
              </p>

              <div className="mb-4 flex gap-2">
                <button
                  onClick={() => setSelectedUserIds(users.filter(u => (u.role === 'admin' || u.role === 'supplier') && u.phone).map(u => u.id))}
                  className="text-xs px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  全選
                </button>
                <button
                  onClick={() => setSelectedUserIds([])}
                  className="text-xs px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  取消全選
                </button>
              </div>

              <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg mb-4">
                {users.filter(u => u.role === 'admin' || u.role === 'supplier').map(user => (
                  <label
                    key={user.id}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${!user.phone ? 'opacity-50' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(user.id)}
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedUserIds(prev => [...prev, user.id]);
                        } else {
                          setSelectedUserIds(prev => prev.filter(id => id !== user.id));
                        }
                      }}
                      disabled={!user.phone}
                      className="w-4 h-4"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium dark:text-white">
                        {user.display_name || user.username}
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({user.username})</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {roleLabels[user.role]} {user.phone ? `• ${user.phone}` : '• 無 WhatsApp'}
                      </div>
                    </div>
                  </label>
                ))}
                {users.filter(u => u.role === 'admin' || u.role === 'supplier').length === 0 && (
                  <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 text-sm">沒有 admin/supplier 用戶</div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  已選擇 {selectedUserIds.length} 位
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowSendModal(false); setSelectedUserIds([]); }}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    取消
                  </button>
                  <button
                    onClick={async () => {
                      if (selectedUserIds.length === 0) {
                        setError('請至少選擇一位用戶');
                        return;
                      }
                      if (!confirm(`確定要重置 ${selectedUserIds.length} 位用戶的密碼並發送到 WhatsApp 嗎？`)) return;
                      setSendLoading(true);
                      try {
                        const res = await fetch(`${API_BASE}/api/admin/users/send-credentials`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                          },
                          body: JSON.stringify({ userIds: selectedUserIds }),
                        });
                        const data = await res.json();
                        if (res.ok) {
                          setSuccess(`已發送 ${data.sent}/${data.total} 位用戶的登入資料`);
                          setShowSendModal(false);
                          setSelectedUserIds([]);
                          setTimeout(() => setSuccess(null), 5000);
                        } else {
                          setError(data.error || '發送失敗');
                        }
                      } catch (e) {
                        setError('發送失敗');
                      } finally {
                        setSendLoading(false);
                      }
                    }}
                    disabled={sendLoading || selectedUserIds.length === 0}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed"
                  >
                    {sendLoading ? '發送中...' : '確認發送'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Permission Preview Modal */}
        {previewUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full">
              <h3 className="text-lg font-bold mb-2 dark:text-white flex items-center gap-2">
                <Shield size={20} className="text-cyan-600" />
                權限預覽
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                用戶：{previewUser.display_name || previewUser.username}（{roleLabels[previewUser.role] || previewUser.role}）
              </p>
              <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg mb-4">
                <table className="min-w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">模組</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">查看</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">編輯/操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {Object.entries(getPermissionMatrix(previewUser.role)).map(([module, flags]) => (
                      <tr key={module}>
                        <td className="px-4 py-2 text-sm text-gray-800 dark:text-gray-200">{MODULE_LABELS[module] || module}</td>
                        <td className="px-4 py-2 text-center">{flags.read ? <span className="text-green-600 font-bold">✓</span> : <span className="text-gray-300">-</span>}</td>
                        <td className="px-4 py-2 text-center">{flags.write ? <span className="text-green-600 font-bold">✓</span> : <span className="text-gray-300">-</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setPreviewUser(null)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  關閉
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
